import { useCurrency } from '@/hooks/useCurrency'
import { DataTable, ReportPage, StatGrid, useReport, exportCsv, type Column } from '@/components/reports/ReportKit'

interface RateRow {
  rate: number
  bills: number
  taxable: number
  cgst: number
  sgst: number
  gst: number
}
interface GstData {
  rates: RateRow[]
  summary: {
    taxableValue: number
    cgst: number
    sgst: number
    totalGst: number
    serviceCharge: number
    inclusiveItems: number
    exclusiveItems: number
    noTaxBills: number
  }
}

export default function GstReportScreen() {
  const { formatCurrency } = useCurrency()
  const report = useReport<GstData>('gst-report')
  const data = report.data

  const columns: Column<RateRow>[] = [
    { key: 'rate', header: 'GST rate', render: r => <span className="font-medium">{r.rate}%</span> },
    { key: 'bills', header: 'Bills', align: 'right' },
    { key: 'taxable', header: 'Taxable value', align: 'right', render: r => formatCurrency(r.taxable) },
    { key: 'cgst', header: 'CGST', align: 'right', render: r => formatCurrency(r.cgst) },
    { key: 'sgst', header: 'SGST', align: 'right', render: r => formatCurrency(r.sgst) },
    { key: 'gst', header: 'Total tax', align: 'right', render: r => formatCurrency(r.gst) }
  ]

  return (
    <ReportPage
      eyebrow="Money · Tax"
      title="GST Report"
      subtitle="Taxable value and CGST / SGST by rate, for your accountant's GSTR-1 / 3B"
      report={report}
      onExport={() => exportCsv('gst-report', columns, data?.rates ?? [])}
    >
      {data && (
        <>
          <StatGrid
            stats={[
              { label: 'Taxable value', value: formatCurrency(data.summary.taxableValue), sub: 'Sub-total less discounts' },
              { label: 'CGST', value: formatCurrency(data.summary.cgst) },
              { label: 'SGST', value: formatCurrency(data.summary.sgst) },
              { label: 'Total GST', value: formatCurrency(data.summary.totalGst), sub: `Service charge ${formatCurrency(data.summary.serviceCharge)}` }
            ]}
          />
          <DataTable
            title="Rate-wise summary"
            columns={columns}
            rows={data.rates}
            rowKey={r => String(r.rate)}
            defaultSort="rate"
            csvName="gst-rate-wise"
            empty="No taxed bills in this period."
          />
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="rounded-lg border p-4">
              <div className="text-xs text-muted-foreground">Items sold tax-inclusive</div>
              <div className="mt-1 text-xl font-bold">{data.summary.inclusiveItems}</div>
            </div>
            <div className="rounded-lg border p-4">
              <div className="text-xs text-muted-foreground">Items sold tax-exclusive</div>
              <div className="mt-1 text-xl font-bold">{data.summary.exclusiveItems}</div>
            </div>
            <div className="rounded-lg border p-4">
              <div className="text-xs text-muted-foreground">Bills with no GST charged</div>
              <div className="mt-1 text-xl font-bold">{data.summary.noTaxBills}</div>
            </div>
          </div>
        </>
      )}
    </ReportPage>
  )
}
